/**
 * UI-32: Control Tower Action Toast — Feedback Layer 1
 *
 * Fixed-position toast for immediate action feedback.
 * Appears within 0–500ms of action execution.
 *
 * Variants:
 *   success — emerald, role="status", aria-live="polite"
 *   error   — rose, role="alert", aria-live="assertive"
 *
 * Rules:
 *   - No technical wording
 *   - No loading spinners
 *   - Dismissible + auto-dismiss after 4s
 */

import type { ActionToastState } from '../../hooks/useActionExecution';

interface ControlTowerToastProps {
  toast: ActionToastState | null;
  onDismiss: () => void;
}

const VARIANT_STYLES = {
  success: 'bg-emerald-900/90 border-emerald-700/60 text-emerald-200',
  warning: 'bg-amber-900/90 border-amber-700/60 text-amber-200',
  error: 'bg-rose-900/90 border-rose-700/60 text-rose-200',
} as const;

const VARIANT_ICONS = {
  success: '✓',
  warning: '!',
  error: '✕',
} as const;

export function ControlTowerToast({ toast, onDismiss }: ControlTowerToastProps) {
  if (!toast) return null;

  const isError = toast.variant === 'error';
  const isWarning = toast.variant === 'warning';
  const styles = VARIANT_STYLES[toast.variant];
  const icon = VARIANT_ICONS[toast.variant];

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError || isWarning ? 'assertive' : 'polite'}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-in-right"
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm ${styles}`}
      >
        <span className="text-sm font-medium">{icon}</span>
        <span className="text-sm">{toast.message}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="ml-2 opacity-60 hover:opacity-100 text-xs transition-opacity"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
